/**
 * prefetchSynthesis — fire-and-forget cache warming.
 *
 * Call on hover over a search result (disease or medication) so the
 * synthesis page is already in cache by the time the user clicks.
 * Silently fails on error — this is a UX accelerator, not a critical
 * path.
 *
 * Two dedupe layers:
 *   1. 30-second per-key memoisation so a hover-storm fires once.
 *   2. Navigation-aware: if the current URL already references the
 *      same target (e.g. user is on /walkthrough?disease=Diabetes and
 *      hovers "Diabetes" in the SmartSearch dropdown), skip — the
 *      page itself is already loading the synthesis.
 *
 * Backed by `POST /api/knowledge/synthesize/prefetch` which returns 202 and
 * runs the actual synthesis in a detached task.
 */
import { api } from "./api";

const _seen = new Map(); // key -> last-ts
const DEDUPE_MS = 30_000;

function _dedupeKey(kind, name, audience, depth) {
  return `${kind}::${(name || "").trim().toLowerCase()}::${audience}::${depth}`;
}

/**
 * Heuristic: does the current page already represent the same target?
 * If so we skip the prefetch — the page is already triggering the
 * real synthesis call and a duplicate scheduled task is wasted work.
 *
 * Looks at common query-param names (`disease`, `med`, `name`, `q`)
 * across Walkthrough, Pathophys, Medications, Therapeutics, etc.
 */
function _alreadyOnPage(name) {
  if (typeof window === "undefined" || !name) return false;
  const target = name.trim().toLowerCase();
  const sp = new URLSearchParams(window.location.search || "");
  for (const param of ["disease", "med", "name", "q"]) {
    const v = (sp.get(param) || "").trim().toLowerCase();
    if (v && v === target) return true;
  }
  // SynthesisPage uses /synth/{kind}?name=... — captured above by `name`.
  // Pathophys, Walkthrough handled by `disease`. Medications by `med`.
  return false;
}

export function prefetchSynthesis({
  kind = "condition",
  name = "",
  audience = "community",
  depth = "plain",
}) {
  const n = (name || "").trim();
  if (n.length < 2) return;
  if (_alreadyOnPage(n)) return;
  const key = _dedupeKey(kind, n, audience, depth);
  const now = Date.now();
  const last = _seen.get(key) || 0;
  if (now - last < DEDUPE_MS) return;
  _seen.set(key, now);
  api
    .post(`/synthesize/prefetch?kind=${encodeURIComponent(kind)}`,
          { name: n, audience, depth })
    .catch(() => { /* fire-and-forget */ });
}
